import React, { useState } from "react";
import { Input, Select } from "antd";

import {
  PROJECT_PRIORITY_OPTIONS,
  PROJECT_STATUS_OPTIONS,
} from "../../constants/projects.constants";

import type { ProjectFilters as ProjectFiltersType } from "../../../../core/data/redux/projects/types";

interface ProjectFiltersProps {
  filters: ProjectFiltersType;
  viewMode: "grid" | "table";
  onFilterChange: (partial: Partial<ProjectFiltersType>) => void;
  onViewModeChange: (mode: "grid" | "table") => void;
  onCreateClick: () => void;
  totalCount?: number;
  isLoading?: boolean;
}

const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  filters,
  viewMode,
  onFilterChange,
  onViewModeChange,
  onCreateClick,
  totalCount = 0,
  isLoading = false,
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || "");

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status !== null && filters.status !== undefined) count++;
    if (filters.priority !== null && filters.priority !== undefined) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  const handleSearch = () => {
    onFilterChange({ search: localSearch.trim(), page: 1 });
  };

  const handleReset = () => {
    setLocalSearch("");
    onFilterChange({
      search: "",
      status: null,
      priority: null,
      page: 1,
    });
  };

  const filterOption = (input: string, option?: { label: string; value: any }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <div className="card">
      <div className="card-header border-0 pb-0">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1 d-flex align-items-center">
              <i className="ti ti-briefcase text-info me-2" style={{ fontSize: 20 }} />
              Projects
            </h4>
            <p className="text-muted mb-0">
              Manage your projects, members, and boards
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-icon btn-sm ${viewMode === "grid" ? "btn-info" : "btn-light"}`}
                onClick={() => onViewModeChange("grid")}
              >
                <i className="ti ti-layout-grid" />
              </button>
              <button
                type="button"
                className={`btn btn-icon btn-sm ${viewMode === "table" ? "btn-info" : "btn-light"}`}
                onClick={() => onViewModeChange("table")}
              >
                <i className="ti ti-list" />
              </button>
            </div>
            <button
              type="button"
              className="btn btn-info btn-sm d-flex align-items-center"
              onClick={onCreateClick}
            >
              <i className="ti ti-plus me-1" />
              New Project
            </button>
          </div>
        </div>
      </div>

      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
        <div className="d-flex gap-2">
          <div className="d-flex gap-2 mb-2 me-2">
            <Input
              placeholder="Search projects..."
              value={localSearch}
              onChange={(e) => {
                const value = e.target.value;
                setLocalSearch(value);
                if (value === "") {
                  onFilterChange({ search: "", page: 1 });
                }
              }}
              onPressEnter={handleSearch}
              style={{ width: 210, minHeight: 38 }}
              allowClear
            />
            <button
              type="button"
              className="btn btn-info"
              onClick={handleSearch}
              style={{ minHeight: 38 }}
            >
              <i className="ti ti-search me-1" />
            </button>
          </div>
        </div>

        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 140, minHeight: 38 }}
              placeholder="Status"
              value={filters.status ?? undefined}
              allowClear
              onChange={(value) => onFilterChange({ status: value ?? null, page: 1 })}
              options={PROJECT_STATUS_OPTIONS}
            />
          </div>
          <div className="me-2 mb-2">
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 140, minHeight: 38 }}
              placeholder="Priority"
              value={filters.priority ?? undefined}
              allowClear
              onChange={(value) => onFilterChange({ priority: value ?? null, page: 1 })}
              options={PROJECT_PRIORITY_OPTIONS}
            />
          </div>
          <div>
            <button
              type="button"
              className={`btn btn-md ${activeFiltersCount > 0 ? "btn-warning" : "btn-outline-danger"} mb-2 position-relative py-2`}
              onClick={handleReset}
            >
              <i className="ti ti-refresh me-1" />
              Reset
              {activeFiltersCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "10px" }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectFilters;
